/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import baseUrl from '../BaseUrl';

const TYPE_OPTIONS = [
  { value: 'post_signup', label: 'Post-Signup' },
  { value: 'paid_user', label: 'Paid User' },
  { value: 'date_specific', label: 'Date-Specific' },
];

const TARGET_AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'paid', label: 'Paid Users' },
  { value: 'free', label: 'Free Users' },
];

const today = new Date();
today.setHours(0, 0, 0, 0);

function formatDateYMD(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const NotificationForm = ({ token, editConfig, onSave, onCancel }) => {
  const [type, setType] = useState('post_signup');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [screenId, setScreenId] = useState('');
  const [dayOffset, setDayOffset] = useState('');
  const [scheduledDate, setScheduledDate] = useState(null);
  const [targetAudience, setTargetAudience] = useState('all');
  const [screens, setScreens] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const isEdit = Boolean(editConfig);

  useEffect(() => {
    fetchScreens();
  }, []);

  useEffect(() => {
    if (editConfig) {
      setType(editConfig.type || 'post_signup');
      setTitle(editConfig.title || '');
      setBody(editConfig.body || '');
      setScreenId(editConfig.screen_id ? String(editConfig.screen_id) : '');

      if (editConfig.type === 'post_signup' || editConfig.type === 'paid_user') {
        setDayOffset(editConfig.day_offset ? String(editConfig.day_offset) : '');
      } else if (editConfig.type === 'date_specific') {
        setTargetAudience(editConfig.target_audience || 'all');
        if (editConfig.scheduled_date) {
          const dateStr = String(editConfig.scheduled_date).slice(0, 10);
          const parts = dateStr.split('-');
          if (parts.length === 3) {
            setScheduledDate(new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])));
          }
        }
      }
    }
  }, [editConfig]);

  const fetchScreens = async () => {
    try {
      const response = await axios.get(`${baseUrl}/admin/api/notification-screens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setScreens(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch notification screens', err);
    }
  };

  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = 'Title is required';
    else if (title.length > 100) errs.title = 'Title must be 100 characters or fewer';

    if (!body.trim()) errs.body = 'Body is required';
    else if (body.length > 500) errs.body = 'Body must be 500 characters or fewer';

    if (!screenId) errs.screenId = 'Screen is required';

    if (type === 'post_signup' || type === 'paid_user') {
      if (!dayOffset) {
        errs.dayOffset = 'Day offset is required';
      } else {
        const n = Number(dayOffset);
        if (!Number.isInteger(n) || n < 1 || n > 365) {
          errs.dayOffset = 'Day offset must be an integer between 1 and 365';
        }
      }
    } else if (type === 'date_specific') {
      if (!scheduledDate) {
        errs.scheduledDate = 'Date is required';
      } else {
        const selected = new Date(scheduledDate);
        selected.setHours(0, 0, 0, 0);
        if (selected < today) {
          errs.scheduledDate = 'Date must be today or in the future';
        }
      }
    }

    return errs;
  };

  const buildPayload = () => {
    const payload = {
      title: title.trim(),
      body: body.trim(),
      screen_id: Number(screenId),
    };

    if (isEdit) {
      payload.id = editConfig.id;
    } else {
      payload.type = type;
    }

    if (type === 'post_signup' || type === 'paid_user') {
      payload.day_offset = Number(dayOffset);
    } else if (type === 'date_specific') {
      payload.scheduled_date = formatDateYMD(scheduledDate);
      payload.target_audience = targetAudience;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const payload = buildPayload();
      let response;
      if (isEdit) {
        response = await axios.put(`${baseUrl}/admin/api/notification-config`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        response = await axios.post(`${baseUrl}/admin/api/notification-config`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      if (response.data.success) {
        setSuccessMsg(response.data.message || (isEdit ? 'Config updated.' : 'Config created.'));
        if (!isEdit) {
          setTitle('');
          setBody('');
          setScreenId('');
          setDayOffset('');
          setScheduledDate(null);
          setTargetAudience('all');
          setType('post_signup');
        }
        onSave();
      } else {
        setErrorMsg(response.data.message || 'An error occurred.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Internal Server Error';
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setErrors({});
    setDayOffset('');
    setScheduledDate(null);
    setTargetAudience('all');
  };

  return (
    <div className="dashboard-box" style={{ marginBottom: '24px', padding: '24px' }}>
      <h3 style={{ marginBottom: '16px', fontWeight: 600 }}>
        {isEdit ? 'Edit Notification Config' : 'Add Notification Config'}
      </h3>

      {successMsg && (
        <div
          role="status"
          style={{
            background: '#d1fae5',
            border: '1px solid #6ee7b7',
            color: '#065f46',
            borderRadius: '6px',
            padding: '10px 14px',
            marginBottom: '14px',
          }}
        >
          {successMsg}
        </div>
      )}

      {errorMsg && (
        <div
          role="alert"
          style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            color: '#991b1b',
            borderRadius: '6px',
            padding: '10px 14px',
            marginBottom: '14px',
          }}
        >
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* Type */}
        <div className="row" style={{ marginBottom: '16px' }}>
          <div className="col-md-4">
            <label htmlFor="notif-type" style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
              Type <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              id="notif-type"
              className="form-control"
              value={type}
              onChange={handleTypeChange}
              disabled={isEdit}
              style={{ opacity: isEdit ? 0.7 : 1 }}
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {isEdit && (
              <small style={{ color: '#6b7280', fontSize: '12px' }}>Type cannot be changed after creation.</small>
            )}
          </div>

          {/* Day field — for post_signup and paid_user */}
          {(type === 'post_signup' || type === 'paid_user') && (
            <div className="col-md-4">
              <label htmlFor="notif-day-offset" style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                {type === 'post_signup' ? 'Day After Signup' : 'Days After Purchase'} <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                id="notif-day-offset"
                type="number"
                className="form-control"
                value={dayOffset}
                min={1}
                max={365}
                placeholder="e.g. 7"
                onChange={(e) => { setDayOffset(e.target.value); setErrors((p) => ({ ...p, dayOffset: undefined })); }}
              />
              {errors.dayOffset && <span style={{ color: 'red', fontSize: '13px' }}>{errors.dayOffset}</span>}
            </div>
          )}

          {/* Date + Target Audience — for date_specific */}
          {type === 'date_specific' && (
            <>
              <div className="col-md-4">
                <label htmlFor="notif-sched-date" style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                  Scheduled Date <span style={{ color: 'red' }}>*</span>
                </label>
                <DatePicker
                  id="notif-sched-date"
                  selected={scheduledDate}
                  onChange={(date) => { setScheduledDate(date); setErrors((p) => ({ ...p, scheduledDate: undefined })); }}
                  dateFormat="dd/MM/yyyy"
                  minDate={today}
                  placeholderText="Select a date"
                  className="form-control"
                />
                {errors.scheduledDate && <span style={{ color: 'red', fontSize: '13px' }}>{errors.scheduledDate}</span>}
              </div>
              <div className="col-md-4">
                <label htmlFor="notif-audience" style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
                  Target Audience
                </label>
                <select
                  id="notif-audience"
                  className="form-control"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                >
                  {TARGET_AUDIENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Screen */}
          <div className="col-md-4">
            <label htmlFor="notif-screen" style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
              Mobile Screen <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              id="notif-screen"
              className="form-control"
              value={screenId}
              onChange={(e) => { setScreenId(e.target.value); setErrors((p) => ({ ...p, screenId: undefined })); }}
            >
              <option value="">-- Select Screen --</option>
              {screens.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.screen_name}
                </option>
              ))}
            </select>
            {errors.screenId && <span style={{ color: 'red', fontSize: '13px' }}>{errors.screenId}</span>}
          </div>
        </div>

        {/* Title */}
        <div className="row" style={{ marginBottom: '16px' }}>
          <div className="col-md-12">
            <label htmlFor="notif-title" style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
              Title <span style={{ color: 'red' }}>*</span>{' '}
              <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '13px' }}>
                ({title.length}/100)
              </span>
            </label>
            <input
              id="notif-title"
              type="text"
              className="form-control"
              value={title}
              maxLength={100}
              placeholder="Notification title"
              onChange={(e) => { setTitle(e.target.value); setErrors((p) => ({ ...p, title: undefined })); }}
            />
            {errors.title && <span style={{ color: 'red', fontSize: '13px' }}>{errors.title}</span>}
          </div>
        </div>

        {/* Body */}
        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-md-12">
            <label htmlFor="notif-body" style={{ fontWeight: 500, display: 'block', marginBottom: '6px' }}>
              Body <span style={{ color: 'red' }}>*</span>{' '}
              <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '13px' }}>
                ({body.length}/500)
              </span>
            </label>
            <textarea
              id="notif-body"
              className="form-control"
              value={body}
              maxLength={500}
              rows={4}
              placeholder="Notification body text"
              onChange={(e) => { setBody(e.target.value); setErrors((p) => ({ ...p, body: undefined })); }}
            />
            {errors.body && <span style={{ color: 'red', fontSize: '13px' }}>{errors.body}</span>}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={submitting}
            style={{
              background: '#FE825B',
              color: '#fff',
              border: '1px solid #FE825B',
              borderRadius: '5px',
              padding: '9px 14px',
              fontSize: '16px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Saving…' : isEdit ? 'Update' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              background: '#fff',
              color: '#FE825B',
              border: '1px solid #FE825B',
              borderRadius: '5px',
              padding: '9px 14px',
              fontSize: '16px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default NotificationForm;
